// @flow

import * as React from 'react';
import ReactDOM from 'react-dom';

import Portal from '@zapier/common-components/src/components/Portal';
import WatchWindowSize from '@zapier/common-components/src/components/WatchWindowSize';
import PopoverInner, {
  type PopoverThemeType,
} from '@zapier/common-components/src/components/Popover/PopoverInner';
import {
  getRef,
  setRef,
  subscribeAnimationFrame,
} from '@zapier/common-components/src/components/Popover/utils';

type Rectangle = {
  bottom: number,
  left: number,
  right: number,
  top: number,
};

type Window = {
  windowHeight: number,
  windowWidth: number,
};

type Props = {
  /** Always display popover above the content */
  alwaysShowAbove: boolean,
  /** Node that Popover will point to */
  children: React.Node,
  /** Popover content Node */
  content: React.Node,
  /** Fade in popover when opened */
  fadeIn?: boolean,
  /** Show as compact popover */
  isCompact?: boolean,
  /** Maximum width */
  maxWidth?: string,
  /** Do not wrap text in popover */
  noWrap?: boolean,
  /** Called on Popover close */
  onClose?: () => null,
  /** Called when mouse enters Popover */
  onMouseEnter?: () => null,
  /** Called when mouse leaves Popover */
  onMouseLeave?: () => null,
  /** Called when Popover moves */
  onMove?: () => null,
  /** Called when target rectangle is being calculated to allow override of
   rectangle position */
  onTargetRect?: ClientRect => Rectangle,
  shouldAttach?: boolean,
  /** Show Popover */
  show?: boolean,
  /** Popover style theme */
  theme?: PopoverThemeType,
  /** z-index */
  zIndex?: number,
};

type State = {
  isOpen: boolean,
  targetRect: ?Rectangle,
};

/**
 * Makes a little bubble of content that points to a target element.
 * - Uses a React portal, so you don't have to worry about it
 * mucking with the markup.
 * - Renders the target component as-is, with no wrapping div or span.
 */
class Popover extends React.PureComponent<Props, State> {
  static defaultProps = {
    alwaysShowAbove: false,
    fadeIn: false,
    isCompact: false,
    noWrap: false,
    shouldAttach: false,
    show: false,
    theme: 'default',
  };

  state = {
    isOpen: false,
    targetRect: null,
  };

  portalNode: ?Element = null;
  targetNode: ?Element = null;
  unsubscribeTargetAnimation: ?any = null;

  componentDidMount() {
    this.updateRefs();
  }

  componentDidUpdate() {
    this.updateRefs();
  }

  componentWillUnmount() {
    this.cancelWatchingTarget();
  }

  attach = ({ left, top }: Rectangle): Object => {
    if (!this.portalNode) return {};
    const portalContainerRect = this.portalNode.getBoundingClientRect();
    return {
      attachTop: top - portalContainerRect.top,
      attachLeft: left - portalContainerRect.left,
    };
  };

  cancelWatchingTarget = () => {
    if (this.unsubscribeTargetAnimation) {
      this.unsubscribeTargetAnimation();
      this.unsubscribeTargetAnimation = null;
    }
  };

  close = () => {
    this.cancelWatchingTarget();
    this.setState({
      isOpen: false,
    });
  };

  findTargetNode(): Element {
    let targetNode = (ReactDOM.findDOMNode(this): any);
    if (this.props.shouldAttach) {
      if (targetNode.childNodes.length > 0) {
        targetNode = targetNode.childNodes[0];
      }
    }
    return targetNode;
  }

  hasPopoverContent = (): boolean =>
    Boolean(this.props.content && this.props.show);

  isTargetVisible = (
    { bottom, left, right, top }: Rectangle,
    { windowHeight, windowWidth }: Window
  ) => {
    return (
      bottom > 0 &&
      bottom < windowHeight &&
      left > 0 &&
      left < windowWidth &&
      right > 0 &&
      right < windowWidth &&
      top > 0 &&
      top < windowHeight
    );
  };

  onUpdate = () => {
    const { isOpen } = this.state;
    if (isOpen && !this.hasPopoverContent()) {
      return this.close();
    }
    if (!isOpen && this.hasPopoverContent()) {
      return this.open();
    }
    return undefined;
  };

  onWatchTarget = () => {
    if (this.targetNode) {
      // Get the current rect for the target.
      const targetRect = this.targetRect() || {
        bottom: -1,
        left: -1,
        right: -1,
        top: -1,
      };
      const lastTargetRect = this.state.targetRect || {
        bottom: -1,
        left: -1,
        right: -1,
        top: -1,
      };
      // Only update if there has been a change in the target
      if (
        targetRect.top !== lastTargetRect.top ||
        targetRect.right !== lastTargetRect.right ||
        targetRect.bottom !== lastTargetRect.bottom ||
        targetRect.left !== lastTargetRect.left
      ) {
        if (this.props.onMove) {
          this.props.onMove();
        }
        this.setState({
          targetRect,
        });
      }
    }
  };

  open = () => {
    this.watchTarget();
    this.setState({
      isOpen: true,
      targetRect: this.targetRect(),
    });
  };

  // $FlowFixMe: Flow thinks document.body could be null but for us it can't
  portalContainer = (): Element => getRef(this, 'portal') || document.body;

  renderPortal = () => {
    const {
      alwaysShowAbove,
      content,
      fadeIn,
      isCompact,
      maxWidth,
      noWrap,
      onClose,
      onMouseEnter,
      onMouseLeave,
      shouldAttach,
      theme,
      zIndex,
    } = this.props;
    // Only render Portal if Popover is open and has content
    if (this.state.isOpen && this.hasPopoverContent()) {
      const targetRect = this.targetRect();
      if (targetRect) {
        // Attach if required
        const { attachLeft, attachTop } = shouldAttach
          ? this.attach(targetRect)
          : {};
        const Wrapper = shouldAttach ? React.Fragment : Portal;
        // Render the popover into the portal
        return (
          <Wrapper>
            <WatchWindowSize>
              {windowSize =>
                this.isTargetVisible(targetRect, windowSize) && (
                  <PopoverInner
                    alwaysShowAbove={alwaysShowAbove}
                    attachLeft={attachLeft}
                    attachTop={attachTop}
                    fadeIn={fadeIn}
                    isAttached={shouldAttach}
                    isCompact={isCompact}
                    maxWidth={maxWidth}
                    noWrap={noWrap}
                    onClose={onClose}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    targetRect={targetRect}
                    theme={theme}
                    zIndex={zIndex}
                    {...windowSize}
                  >
                    {content}
                  </PopoverInner>
                )
              }
            </WatchWindowSize>
          </Wrapper>
        );
      }
    }
    return null;
  };

  /**
   * Gets target rectangle bounds as an object
   */
  targetRect = (): ?Rectangle => {
    if (this.targetNode) {
      let rect = this.targetNode.getBoundingClientRect();
      if (this.props.onTargetRect) {
        rect = this.props.onTargetRect(rect);
      }
      const { bottom, left, right, top } = rect;
      return {
        bottom,
        left,
        right,
        top,
      };
    }
    return null;
  };

  updateRefs = () => {
    this.targetNode = this.findTargetNode();
    this.portalNode = getRef(this, 'portal');
    this.onUpdate();
  };

  /**
   * Polling for target position while popover is open. Yucky, but safest way
   * to guarantee we know its current rect.
   */
  watchTarget = () => {
    if (!this.unsubscribeTargetAnimation) {
      this.unsubscribeTargetAnimation = subscribeAnimationFrame(
        this.onWatchTarget
      );
    }
  };

  render() {
    const { children, shouldAttach } = this.props;
    // Render portal if we are in the open state
    const portal = this.renderPortal();
    // Render relatively positioned span if attached
    const base = shouldAttach ? (
      <span style={{ position: 'relative' }}>
        {children}
        <div
          ref={setRef(this, 'portal')}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
          }}
        >
          {portal}
        </div>
      </span>
    ) : (
      children
    );

    return (
      <React.Fragment>
        {base}
        {portal}
      </React.Fragment>
    );
  }
}

export default Popover;
